import { DateTime } from "luxon";

export type FormatStyle = "long" | "compact" | "short" | "numeric" | "all";

export type DateStringFormats = {
  full?: string;
  compact?: string;
  short?: string;
  numeric?: string;
  iso?: string;
  time12?: string;
  time24?: string;
  relative?: string | null;
  weekday?: string;
  range?: {
    full?: string;
    compact?: string;
    short?: string;
    numeric?: string;
    iso?: string;
    weekday?: { start: string; end: string };
    relative?: { start: string; end: string };
  };
};

export type DateStringOptions = {
  locale?: string;
  timeZone?: string;
  style?: FormatStyle;
  timeOnly?: boolean;
  showWeekday?: boolean;
  relativeTo?: Date | string;
};

export function parseSanityDateAsCET(dateString: string): Date {
  // return DateTime.fromISO(dateString, { zone: "Europe/Zurich" }).toJSDate();
  return new Date(dateString);
}

export default function dateString(
  rawStartDate: number | string | Date = new Date(),
  rawEndDate?: number | string | Date,
  options: DateStringOptions = {},
): DateStringFormats {
  const {
    locale = "en-US",
    timeZone = "UTC",
    style = "all",
    timeOnly = false,
    showWeekday = false,
    relativeTo,
  } = options;

  // const normalizeDate = (date: number | string | Date = new Date()) =>
  //   typeof date === "string"
  //     ? new Date(
  //         /^\d{2}:\d{2}:\d{2}$/.test(date)
  //           ? `${new Date().toISOString().split("T")[0]}T${date}`
  //           : date.replace(" ", "T")
  //       )
  //     : date;
  const normalizeDate = (date: number | string | Date = new Date()) => {
    if (typeof date === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return new Date(`${date}T12:00:00`);
      }
      if (/^\d{2}:\d{2}:\d{2}$/.test(date)) {
        return new Date(`${new Date().toISOString().split("T")[0]}T${date}`);
      }
      return new Date(date.replace(" ", "T"));
    }
    return date;
  };

  const newStartDate = normalizeDate(rawStartDate);
  const newEndDate = normalizeDate(rawEndDate);

  const referenceDateTime = relativeTo
    ? DateTime.fromJSDate(
        typeof relativeTo === "string" ? new Date(relativeTo) : relativeTo,
      )
    : DateTime.now();

  const startDate =
    typeof newStartDate === "string"
      ? parseSanityDateAsCET(newStartDate)
      : new Date(newStartDate);

  const endDate =
    typeof newEndDate === "string"
      ? parseSanityDateAsCET(newEndDate)
      : newEndDate
        ? new Date(newEndDate)
        : null;

  const baseDateFormatOptions = timeZone ? { timeZone } : {};

  const longDateFormat: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...baseDateFormatOptions,
  };

  const compactDateFormat: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...baseDateFormatOptions,
  };

  const shortDateFormat: Intl.DateTimeFormatOptions = {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    ...baseDateFormatOptions,
  };

  const numericDateFormat: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    ...baseDateFormatOptions,
  };

  const formatDateParts = (date: Date) => {
    const dateTime = DateTime.fromJSDate(date).setZone("Europe/Zurich");

    const weekday = showWeekday
      ? dateTime.setLocale(locale).toFormat("cccc")
      : undefined;

    const relative = dateTime.toRelative({
      base: referenceDateTime,
      locale,
    });

    return {
      full:
        style === "all" || style === "long"
          ? date.toLocaleDateString(locale, longDateFormat)
          : undefined,
      compact:
        style === "all" || style === "compact"
          ? date.toLocaleDateString(locale, compactDateFormat)
          : undefined,
      short:
        style === "all" || style === "short"
          ? date.toLocaleDateString(locale, shortDateFormat)
          : undefined,
      numeric:
        style === "all" || style === "numeric"
          ? date.toLocaleDateString(locale, numericDateFormat)
          : undefined,
      iso: style === "all" ? date.toISOString() : undefined,
      weekday,
      relative,
    };
  };

  const formatTimeAcrossZones = (
    date: Date,
    endDate?: Date,
  ): { time12: string; time24: string } => {
    const timezones = [
      { label: "CET", tz: "Europe/Zurich" },
      { label: "PST", tz: "America/Los_Angeles" },
      { label: "EST", tz: "America/New_York" },
    ];

    const formattedTime12List = timezones.map(({ label, tz }) => {
      const time = date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: tz,
      });
      return `${time} ${label}`;
    });

    const formattedTime24List = timezones.map(({ label, tz }) => {
      const time = date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: tz,
      });
      return `${time} ${label}`;
    });

    let durationLabel = "";
    if (endDate && date.toDateString() === endDate.toDateString()) {
      const durationInMs = Math.abs(endDate.getTime() - date.getTime());
      const totalMinutes = Math.floor(durationInMs / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      const hoursLabel =
        hours > 0 ? `${hours} hour${hours !== 1 ? "s" : ""}` : "";
      const minutesLabel =
        minutes > 0 ? `${minutes} minute${minutes !== 1 ? "s" : ""}` : "";
      const spacer = hoursLabel && minutesLabel ? " " : "";
      const hasDuration = hoursLabel || minutesLabel;

      if (hasDuration) {
        durationLabel = ` (${hoursLabel}${spacer}${minutesLabel})`;
      }
    }

    return {
      time12: `${formattedTime12List.join(" | ")}${durationLabel}`,
      time24: `${formattedTime24List.join(" | ")}${durationLabel}`,
    };
  };

  const startDateFormatted = {
    ...formatDateParts(startDate),
    ...formatTimeAcrossZones(startDate, endDate ?? undefined),
  };

  if (timeOnly) {
    return {
      time12: startDateFormatted.time12,
      time24: startDateFormatted.time24,
    };
  }

  if (endDate) {
    const endDateFormatted = formatDateParts(endDate);

    const isSameDay = startDate.toDateString() === endDate.toDateString();
    const isSameMonth =
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();
    const isSameYear = startDate.getFullYear() === endDate.getFullYear();

    let fullRangeLabel: string | undefined;

    if (style === "all" || style === "long") {
      if (isSameDay) {
        fullRangeLabel = startDateFormatted.full;
      } else if (isSameMonth) {
        const monthName = startDate.toLocaleDateString(locale, {
          month: "long",
          ...baseDateFormatOptions,
        });
        fullRangeLabel = `${monthName} ${startDate.getDate()}–${endDate.getDate()}, ${startDate.getFullYear()}`;
      } else if (isSameYear) {
        const rangeStart = startDate.toLocaleDateString(locale, {
          month: "long",
          day: "numeric",
          ...baseDateFormatOptions,
        });
        const rangeEnd = endDate.toLocaleDateString(locale, {
          month: "long",
          day: "numeric",
          ...baseDateFormatOptions,
        });
        fullRangeLabel = `${rangeStart} – ${rangeEnd}, ${startDate.getFullYear()}`;
      } else {
        fullRangeLabel = `${startDateFormatted.full} – ${endDateFormatted.full}`;
      }
    }

    return {
      ...startDateFormatted,
      range: {
        full: fullRangeLabel,
        short:
          startDateFormatted.short && endDateFormatted.short
            ? `${startDateFormatted.short} – ${endDateFormatted.short}`
            : undefined,
        numeric:
          startDateFormatted.numeric && endDateFormatted.numeric
            ? `${startDateFormatted.numeric} – ${endDateFormatted.numeric}`
            : undefined,
        iso:
          startDateFormatted.iso && endDateFormatted.iso
            ? `${startDateFormatted.iso} – ${endDateFormatted.iso}`
            : undefined,
        weekday:
          startDateFormatted.weekday && endDateFormatted.weekday
            ? {
                start: startDateFormatted.weekday,
                end: endDateFormatted.weekday,
              }
            : undefined,
        relative:
          startDateFormatted.relative && endDateFormatted.relative
            ? {
                start: startDateFormatted.relative,
                end: endDateFormatted.relative,
              }
            : undefined,
      },
    };
  }

  return startDateFormatted;
}

export function rangeLabel(
  dateInfo: DateStringFormats,
  includeRelative = true,
): string | undefined {
  const range = dateInfo.range;
  if (!range || !range.full) return undefined;

  const weekdayStart = range.weekday?.start;
  const weekdayEnd = range.weekday?.end;
  const relativeStart = includeRelative ? range.relative?.start : "";
  const relativeEnd = includeRelative ? range.relative?.end : "";
  const durationMatch = dateInfo.time24?.match(/([^)]+)$/);

  const fullStart =
    weekdayStart && dateInfo.full
      ? `${weekdayStart}, ${dateInfo.full}`
      : dateInfo.full;

  const fullEnd =
    weekdayEnd && range.full && range.full !== dateInfo.full
      ? `${weekdayEnd}, ${range.full?.split(" – ")[1] ?? ""}`
      : undefined;

  const base =
    fullStart && fullEnd && fullStart !== fullEnd
      ? `${fullStart} – ${fullEnd}`
      : fullStart;

  const relativeSuffix =
    fullEnd && relativeStart && relativeEnd && relativeStart === relativeEnd
      ? ` (${relativeStart})`
      : relativeStart && !fullEnd
        ? ` (${relativeStart})`
        : "";

  const durationSuffix = durationMatch ? ` (${durationMatch[1]})` : "";

  return `${base}${relativeSuffix || durationSuffix}`;
}

// Older CMS entries predate the validation that blocks an Updated Date earlier
// than the Published Date, so those are ignored rather than displayed.
export function validUpdatedAt(updatedAt?: string, publishedAt?: string) {
  if (!updatedAt || !publishedAt) return updatedAt;
  return new Date(updatedAt).getTime() >= new Date(publishedAt).getTime()
    ? updatedAt
    : undefined;
}

type DatedDocument = {
  updatedAt?: string;
  publishedAt?: string;
  _updatedAt: string;
};

export function resolveModifiedAt(doc: DatedDocument) {
  return validUpdatedAt(doc?.updatedAt, doc?.publishedAt) || doc?._updatedAt;
}
